import React, { useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Badge,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import MarkdownEditor from '~/components/Common/MarkdownEditor';

import {
  ConfigurationEntry,
  ConfigurationHighlight,
  ConfigurationInclusion,
  INCLUSION_CATEGORY_LABELS,
  INCLUSION_KIND_LABELS,
  InclusionCategory,
  InclusionKind,
  TourOfferConfiguration,
  TourOfferConfigurationContent,
} from '~/types/tours/tourOffer';

interface Props {
  configuration: TourOfferConfiguration;
  value: TourOfferConfigurationContent;
  onChange: (value: TourOfferConfigurationContent) => void;
  /** Off where the itinerary has an editor of its own. */
  showItinerary?: boolean;
  /** Shows just these parts, one under the other with headings, instead of tabs. */
  sections?: Array<ConfigurationEditorSection>;
}

export type ConfigurationEditorSection = 'about' | 'highlights' | 'inclusions' | 'itinerary';
type Area = ConfigurationEditorSection;

const SECTION_HEADINGS: Record<Area, string> = {
  about: 'About',
  highlights: 'Highlights',
  inclusions: "What's included",
  itinerary: 'Itinerary',
};

const MEAL_OPTIONS = ['Breakfast', 'Lunch', 'Dinner'];
// Itinerary text is a list of paragraphs; one blank line separates them in the field.
const PARAGRAPH_BREAK = '\n\n';
const CATEGORIES = Object.keys(INCLUSION_CATEGORY_LABELS) as Array<InclusionCategory>;
const KINDS = Object.keys(INCLUSION_KIND_LABELS) as Array<InclusionKind>;

function RemoveButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Tooltip title="Remove">
      <IconButton aria-label={label} onClick={onClick}>
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="body2" color="text.secondary">
      {children}
    </Typography>
  );
}

const isHighlightValid = (entry: ConfigurationHighlight) => !!entry.title.trim() && !!entry.description.trim();
const isInclusionValid = (entry: ConfigurationInclusion) => !!entry.title.trim();

/** Whether every highlight, inclusion and itinerary day has what svc-tour requires. */
export function isConfigurationContentValid(value: TourOfferConfigurationContent): boolean {
  return (
    value.inclusions.every((entry) =>
      entry.type === 'highlight' ? isHighlightValid(entry) : isInclusionValid(entry),
    ) && value.itineraries.every((day) => !!day.title.trim())
  );
}

/** Drops the empty paragraphs the text field leaves while typing. */
export function cleanConfigurationContent(value: TourOfferConfigurationContent): TourOfferConfigurationContent {
  return {
    ...value,
    itineraries: value.itineraries.map((day) => ({ ...day, text: day.text.filter((paragraph) => paragraph.trim()) })),
  };
}

/** One package option's copy of the base tour: about, highlights, inclusions and itinerary. */
export default function ConfigurationEditor({ configuration, value, onChange, showItinerary = true, sections }: Props) {
  const [area, setArea] = useState<Area>('about');
  const [openDay, setOpenDay] = useState<string | false>(false);

  const highlights = value.inclusions.filter((entry): entry is ConfigurationHighlight => entry.type === 'highlight');
  const inclusions = value.inclusions.filter((entry): entry is ConfigurationInclusion => entry.type === 'inclusion');
  const startDays = new Map(configuration.itineraries.map((day) => [day.id, day.startDay]));

  const set = (change: Partial<TourOfferConfigurationContent>) => onChange({ ...value, ...change });
  const updateEntry = (id: string, change: Partial<ConfigurationHighlight> | Partial<ConfigurationInclusion>) =>
    set({
      inclusions: value.inclusions.map((entry) =>
        entry.id === id ? ({ ...entry, ...change } as ConfigurationEntry) : entry,
      ),
    });
  const removeEntry = (id: string) => set({ inclusions: value.inclusions.filter((entry) => entry.id !== id) });
  const addEntry = (entry: ConfigurationEntry) => set({ inclusions: [...value.inclusions, entry] });
  const updateDay = (id: string, change: Partial<TourOfferConfigurationContent['itineraries'][number]>) =>
    set({ itineraries: value.itineraries.map((day) => (day.id === id ? { ...day, ...change } : day)) });

  const invalid: Record<Area, boolean> = {
    about: false,
    highlights: highlights.some((entry) => !isHighlightValid(entry)),
    inclusions: inclusions.some((entry) => !isInclusionValid(entry)),
    itinerary: value.itineraries.some((day) => !day.title.trim()),
  };
  const areas: ReadonlyArray<{ key: Area; label: string }> = [
    { key: 'about', label: 'About' },
    { key: 'highlights', label: `Highlights (${highlights.length})` },
    { key: 'inclusions', label: `What's included (${inclusions.length})` },
    ...(showItinerary ? [{ key: 'itinerary' as const, label: `Itinerary (${value.itineraries.length} days)` }] : []),
  ];

  const shown: Array<Area> = sections ?? [area];
  const heading = (key: Area) =>
    sections && sections.length > 1 ? (
      <Typography variant="subtitle1" fontWeight="medium">
        {SECTION_HEADINGS[key]}
      </Typography>
    ) : null;

  return (
    <Stack spacing={sections ? 4 : 2}>
      {!sections && (
        <Tabs
          value={area}
          onChange={(_event, next: Area) => setArea(next)}
          variant="scrollable"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {areas.map(({ key, label }) => (
            <Tab
              key={key}
              value={key}
              label={
                <Badge color="error" variant="dot" invisible={!invalid[key]}>
                  <Box pr={invalid[key] ? 1 : 0}>{label}</Box>
                </Badge>
              }
            />
          ))}
        </Tabs>
      )}

      {shown.includes('about') && (
        <Stack spacing={2}>
          {heading('about')}
          <TextField
            label="Title"
            value={value.title ?? ''}
            onChange={(event) => set({ title: event.target.value || null })}
            helperText={`Shown as the heading for ${configuration.name}`}
            fullWidth
          />
          <MarkdownEditor
            label="About: the overview paragraph on the offer page"
            showLabel
            value={value.description ?? ''}
            onChange={(description) => set({ description: description.trim() ? description : null })}
          />
        </Stack>
      )}

      {shown.includes('highlights') && (
        <Stack spacing={1.5}>
          {heading('highlights')}
          <Typography variant="body2" color="text.secondary">
            The standout moments listed near the top of the offer page. Each needs a title and a short description.
          </Typography>
          {highlights.length === 0 && <Empty>No highlights yet.</Empty>}
          {highlights.map((entry, index) => (
            <Paper key={entry.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Typography variant="subtitle2" color="text.secondary" pt={1} minWidth={20}>
                  {index + 1}
                </Typography>
                <Stack spacing={1.5} flex={1}>
                  <TextField
                    size="small"
                    label="Highlight"
                    value={entry.title}
                    onChange={(event) => updateEntry(entry.id, { title: event.target.value })}
                    error={!entry.title.trim()}
                    helperText={!entry.title.trim() ? 'Required' : undefined}
                    fullWidth
                  />
                  <MarkdownEditor
                    label="Description"
                    showLabel
                    value={entry.description}
                    onChange={(description) => updateEntry(entry.id, { description })}
                  />
                  {!entry.description.trim() && (
                    <Typography variant="caption" color="error">
                      A description is required
                    </Typography>
                  )}
                </Stack>
                <RemoveButton label="Remove highlight" onClick={() => removeEntry(entry.id)} />
              </Stack>
            </Paper>
          ))}
          <Box>
            <Button
              startIcon={<AddIcon />}
              onClick={() => addEntry({ type: 'highlight', id: crypto.randomUUID(), title: '', description: '' })}
            >
              Add highlight
            </Button>
          </Box>
        </Stack>
      )}

      {shown.includes('inclusions') && (
        <Stack spacing={2.5}>
          {heading('inclusions')}
          <Typography variant="body2" color="text.secondary">
            Everything the package includes, grouped by category on the offer page.
          </Typography>
          {inclusions.length === 0 && <Empty>Nothing listed yet.</Empty>}
          {CATEGORIES.filter((category) => inclusions.some((entry) => entry.category === category)).map((category) => (
            <Stack key={category} spacing={1}>
              <Typography variant="overline" color="text.secondary" lineHeight={1.5}>
                {INCLUSION_CATEGORY_LABELS[category]}
              </Typography>
              {inclusions
                .filter((entry) => entry.category === category)
                .map((entry) => (
                  <Stack
                    key={entry.id}
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    alignItems={{ sm: 'flex-start' }}
                  >
                    <TextField
                      size="small"
                      label="Inclusion"
                      value={entry.title}
                      onChange={(event) => updateEntry(entry.id, { title: event.target.value })}
                      error={!entry.title.trim()}
                      helperText={!entry.title.trim() ? 'Required' : undefined}
                      fullWidth
                    />
                    <TextField
                      select
                      size="small"
                      label="Category"
                      value={entry.category}
                      onChange={(event) => updateEntry(entry.id, { category: event.target.value as InclusionCategory })}
                      sx={{ minWidth: 200 }}
                    >
                      {CATEGORIES.map((option) => (
                        <MenuItem key={option} value={option}>
                          {INCLUSION_CATEGORY_LABELS[option]}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      size="small"
                      label="Shown as"
                      value={entry.kind}
                      onChange={(event) => updateEntry(entry.id, { kind: event.target.value as InclusionKind })}
                      sx={{ minWidth: 150 }}
                    >
                      {KINDS.map((option) => (
                        <MenuItem key={option} value={option}>
                          {INCLUSION_KIND_LABELS[option]}
                        </MenuItem>
                      ))}
                    </TextField>
                    <RemoveButton label="Remove inclusion" onClick={() => removeEntry(entry.id)} />
                  </Stack>
                ))}
            </Stack>
          ))}
          <Box>
            <Button
              startIcon={<AddIcon />}
              onClick={() =>
                addEntry({ type: 'inclusion', id: crypto.randomUUID(), title: '', category: 'other', kind: 'standard' })
              }
            >
              Add inclusion
            </Button>
          </Box>
        </Stack>
      )}

      {shown.includes('itinerary') && (
        <Stack spacing={1}>
          {heading('itinerary')}
          <Typography variant="body2" color="text.secondary" mb={0.5}>
            Edit each day's title, description, accommodation and meals. Adding, removing and reordering days, and day
            images, come next.
          </Typography>
          {value.itineraries.length === 0 && <Empty>No itinerary days.</Empty>}
          {value.itineraries.map((day) => (
            <Accordion
              key={day.id}
              variant="outlined"
              disableGutters
              expanded={openDay === day.id}
              onChange={(_event, isOpen) => setOpenDay(isOpen ? day.id : false)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" rowGap={0.5}>
                  <Typography variant="subtitle2" color="text.secondary" minWidth={52}>
                    Day {startDays.get(day.id)}
                  </Typography>
                  <Typography variant="subtitle2" color={day.title.trim() ? 'text.primary' : 'error'}>
                    {day.title.trim() || 'Needs a title'}
                  </Typography>
                  {day.meals.map((meal) => (
                    <Chip key={meal} size="small" variant="outlined" label={meal} />
                  ))}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <TextField
                    size="small"
                    label="Title"
                    value={day.title}
                    onChange={(event) => updateDay(day.id, { title: event.target.value })}
                    error={!day.title.trim()}
                    helperText={!day.title.trim() ? 'Required' : undefined}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    label="Description"
                    value={day.text.join(PARAGRAPH_BREAK)}
                    onChange={(event) => updateDay(day.id, { text: event.target.value.split(PARAGRAPH_BREAK) })}
                    helperText="Leave a blank line between paragraphs"
                    multiline
                    minRows={4}
                    fullWidth
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <TextField
                      size="small"
                      label="Accommodation"
                      value={day.accommodation ?? ''}
                      onChange={(event) => updateDay(day.id, { accommodation: event.target.value || null })}
                      fullWidth
                    />
                    <Autocomplete
                      multiple
                      freeSolo
                      size="small"
                      options={MEAL_OPTIONS}
                      value={day.meals}
                      onChange={(_event, meals) => updateDay(day.id, { meals })}
                      renderTags={(meals, getTagProps) =>
                        meals.map((meal, index) => (
                          <Chip {...getTagProps({ index })} key={meal} size="small" label={meal} />
                        ))
                      }
                      renderInput={(params) => <TextField {...params} label="Meals" />}
                      sx={{ minWidth: { sm: 280 } }}
                    />
                  </Stack>
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
